import { isMobileDevice } from './mobileDetect';

export interface GamepadState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  jump: boolean;
  fire: boolean;
}

export interface VirtualGamepadCallbacks {
  onStateChange: (state: GamepadState) => void;
  onJump: () => void;
  onBackflip: () => void;
  onFireStart: () => void;
  onFireEnd: () => void;
  onWeaponToggle: () => void;
  onFuseChange?: () => void;
  /** Drag-to-shoot circle (GunBound-style fire input). */
  onShootDragStart?: () => void;
  onShootDragMove?: (power: number) => void;
  onShootDragRelease?: () => void;
  onShootDragCancel?: () => void;
}

export class VirtualGamepad {
  private container: HTMLElement;
  private dpadContainer: HTMLElement;
  private buttonsContainer: HTMLElement;
  private topButtonsContainer: HTMLElement;
  private shootCircle: HTMLElement;
  private callbacks: VirtualGamepadCallbacks;
  private fuseTimerElement: HTMLElement | null = null;
  private currentFuseTimer: number = 3;
  private shootTouchId: number | null = null;
  private shootStartX = 0;
  /**
   * Horizontal drag distance for full power: half the viewport width,
   * captured at drag start (viewport size changes on rotation/resize).
   */
  private shootDragRange = 220;

  private state: GamepadState = {
    left: false,
    right: false,
    up: false,
    down: false,
    jump: false,
    fire: false,
  };

  private dpadTouches: Map<string, number> = new Map();
  private jumpTouchId: number | null = null;
  private fireTouchId: number | null = null;

  private lastJumpTime: number = 0;
  private jumpTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly DOUBLE_TAP_THRESHOLD = 250;

  constructor(parentElement: HTMLElement, callbacks: VirtualGamepadCallbacks) {
    this.callbacks = callbacks;
    this.container = this.createContainer();
    this.dpadContainer = this.createDpad();
    this.buttonsContainer = this.createButtons();
    this.topButtonsContainer = this.createTopButtons();
    this.shootCircle = this.createShootCircle();

    this.container.appendChild(this.dpadContainer);
    this.container.appendChild(this.buttonsContainer);
    this.container.appendChild(this.topButtonsContainer);
    this.container.appendChild(this.shootCircle);
    parentElement.appendChild(this.container);

    if (!isMobileDevice()) {
      this.hide();
    }
  }

  /**
   * DragonBound-style drag-to-shoot: touch the circle to start the shot,
   * drag right to fill the power bar, drag left to unfill, release to fire.
   */
  private createShootCircle(): HTMLElement {
    const circle = document.createElement('div');
    circle.className = 'btn-shoot-drag';
    circle.style.cssText = `
      position: absolute;
      left: 16px;
      bottom: calc(150px + env(safe-area-inset-bottom, 0px));
      width: 110px;
      height: 110px;
      background: radial-gradient(circle, rgba(255, 210, 90, 0.85), rgba(233, 154, 28, 0.62));
      border: 3px solid rgba(255, 224, 120, 0.95);
      border-radius: 50%;
      display: none;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-size: 15px;
      font-weight: bold;
      color: white;
      text-shadow: 0 1px 3px rgba(0,0,0,0.6);
      box-shadow: 0 0 20px rgba(255, 198, 59, 0.55);
      pointer-events: auto;
      touch-action: none;
      user-select: none;
    `;
    circle.innerHTML = 'Drag me<br>to shoot &#8658;';

    circle.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.shootTouchId !== null) {
          // Self-heal: if the tracked finger is no longer on the screen, its
          // end event never reached us (hidden mid-drag, browser gesture, …).
          // Cancel the stale drag instead of refusing all future shots.
          const stillActive = Array.from(e.touches).some((t) => t.identifier === this.shootTouchId);
          if (stillActive) return;
          this.endShootDrag(true);
        }
        const touch = e.changedTouches[0];
        if (!touch) return;
        this.shootTouchId = touch.identifier;
        this.shootStartX = touch.clientX;
        this.shootDragRange = Math.max(150, window.innerWidth * 0.5);
        circle.style.opacity = '0.6';
        this.vibrate(15);
        this.attachShootWindowListeners();
        this.callbacks.onShootDragStart?.();
        this.callbacks.onShootDragMove?.(0);
      },
      { passive: false },
    );

    return circle;
  }

  /**
   * Move/end handling for the shoot drag lives on window: implicit touch
   * capture usually routes events back to the circle, but a vigorous drag
   * across other UI or off-viewport can drop them, leaving the drag stuck.
   * Window listeners always see the touch end.
   */
  private shootWindowHandlers: Array<[string, (e: TouchEvent) => void]> | null = null;

  private attachShootWindowListeners(): void {
    if (this.shootWindowHandlers) return;
    const onMove = (e: TouchEvent) => {
      const touch = Array.from(e.changedTouches).find((t) => t.identifier === this.shootTouchId);
      if (!touch) return;
      e.preventDefault();
      const power = Math.max(
        0,
        Math.min(100, ((touch.clientX - this.shootStartX) / this.shootDragRange) * 100),
      );
      this.callbacks.onShootDragMove?.(power);
    };
    const onEnd = (e: TouchEvent) => {
      if (this.shootTouchId === null) return;
      const ended = Array.from(e.changedTouches).some((t) => t.identifier === this.shootTouchId);
      const stillActive = Array.from(e.touches).some((t) => t.identifier === this.shootTouchId);
      if (ended || !stillActive) {
        this.endShootDrag(e.type === 'touchcancel');
      }
    };
    this.shootWindowHandlers = [
      ['touchmove', onMove],
      ['touchend', onEnd],
      ['touchcancel', onEnd],
    ];
    for (const [type, handler] of this.shootWindowHandlers) {
      window.addEventListener(type, handler as EventListener, { passive: false, capture: true });
    }
  }

  private detachShootWindowListeners(): void {
    if (!this.shootWindowHandlers) return;
    for (const [type, handler] of this.shootWindowHandlers) {
      window.removeEventListener(type, handler as EventListener, { capture: true });
    }
    this.shootWindowHandlers = null;
  }

  /** Single exit point for the shoot drag: always restores a usable state. */
  private endShootDrag(cancelled: boolean): void {
    if (this.shootTouchId === null) return;
    this.shootTouchId = null;
    this.shootCircle.style.opacity = '1';
    this.detachShootWindowListeners();
    if (cancelled) {
      this.callbacks.onShootDragCancel?.();
    } else {
      this.callbacks.onShootDragRelease?.();
    }
  }

  /** Show/hide the drag-to-shoot circle (drag fire mode, own turn only). */
  setShootDragVisible(visible: boolean): void {
    const display = visible ? 'flex' : 'none';
    if (this.shootCircle.style.display !== display) {
      this.shootCircle.style.display = display;
      if (!visible) {
        this.endShootDrag(true);
      }
      // The FIRE button is redundant while the drag circle is the firing
      // mechanism; it comes back when the circle hides (targeting weapons,
      // sheep detonation, rope release, classic mode).
      const fireBtn = this.buttonsContainer.querySelector('.btn-fire') as HTMLElement | null;
      if (fireBtn) {
        fireBtn.style.display = visible ? 'none' : 'flex';
      }
    }
  }

  /**
   * Drag fire mode layout (DragonBound-style): movement/aim d-pad on the
   * right, jump/fire buttons on the left, shoot circle on the left middle.
   */
  setDragShotLayout(enabled: boolean): void {
    if (enabled) {
      this.dpadContainer.style.left = 'auto';
      this.dpadContainer.style.right = '12px';
      this.buttonsContainer.style.right = 'auto';
      this.buttonsContainer.style.left = '12px';
    } else {
      this.dpadContainer.style.left = '12px';
      this.dpadContainer.style.right = 'auto';
      this.buttonsContainer.style.right = '12px';
      this.buttonsContainer.style.left = 'auto';
    }
  }

  private createContainer(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'virtual-gamepad';
    container.style.cssText = `
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      z-index: 1000;
      user-select: none;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
      touch-action: none;
    `;
    return container;
  }

  /**
   * GunBound-style bottom-left button block: small square FUSE and weapon
   * buttons sitting at the left end of the bottom power-bar strip (the game
   * reserves this space when laying out the power bar).
   */
  private createTopButtons(): HTMLElement {
    const topButtons = document.createElement('div');
    topButtons.className = 'gamepad-top-buttons';
    topButtons.style.cssText = `
      position: absolute;
      left: 8px;
      bottom: calc(8px + env(safe-area-inset-bottom, 0px));
      pointer-events: none;
      touch-action: none;
      display: flex;
      flex-direction: row;
      gap: 6px;
      align-items: center;
    `;

    const fuseBtn = document.createElement('div');
    fuseBtn.className = 'btn-fuse';
    fuseBtn.style.cssText = `
      width: 44px;
      height: 44px;
      background: rgba(233, 154, 28, 0.88);
      border: 2px solid rgba(255, 216, 96, 0.95);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      font-size: 15px;
      font-weight: bold;
      color: white;
      text-shadow: 0 1px 3px rgba(0,0,0,0.5);
      transition: background 0.05s, transform 0.05s;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      pointer-events: auto;
      touch-action: none;
    `;
    fuseBtn.innerHTML = `<span style="font-size:9px">FUSE</span><span class="fuse-value">${this.currentFuseTimer}s</span>`;
    this.fuseTimerElement = fuseBtn.querySelector('.fuse-value');

    const weaponBtn = document.createElement('div');
    weaponBtn.className = 'btn-weapon';
    weaponBtn.style.cssText = `
      width: 44px;
      height: 44px;
      background: rgba(47, 99, 184, 0.82);
      border: 2px solid rgba(91, 155, 232, 0.95);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      color: white;
      text-shadow: 0 1px 3px rgba(0,0,0,0.5);
      transition: background 0.05s, transform 0.05s;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      pointer-events: auto;
      touch-action: none;
    `;
    weaponBtn.textContent = '🔫';

    topButtons.appendChild(fuseBtn);
    topButtons.appendChild(weaponBtn);

    fuseBtn.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
        fuseBtn.style.background = 'rgba(255, 216, 96, 0.98)';
        fuseBtn.style.transform = 'scale(0.9)';
        this.vibrate(10);
        this.callbacks.onFuseChange?.();
      },
      { passive: false },
    );

    fuseBtn.addEventListener(
      'touchend',
      (e) => {
        e.preventDefault();
        fuseBtn.style.background = 'rgba(233, 154, 28, 0.88)';
        fuseBtn.style.transform = 'scale(1)';
      },
      { passive: false },
    );

    weaponBtn.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
        weaponBtn.style.background = 'rgba(91, 155, 232, 0.98)';
        weaponBtn.style.transform = 'scale(0.9)';
        this.vibrate(10);
        this.callbacks.onWeaponToggle();
      },
      { passive: false },
    );

    weaponBtn.addEventListener(
      'touchend',
      (e) => {
        e.preventDefault();
        weaponBtn.style.background = 'rgba(47, 99, 184, 0.82)';
        weaponBtn.style.transform = 'scale(1)';
      },
      { passive: false },
    );

    return topButtons;
  }

  updateFuseTimer(value: number): void {
    this.currentFuseTimer = value;
    if (this.fuseTimerElement) {
      this.fuseTimerElement.textContent = `${value}s`;
    }
  }

  private createDpad(): HTMLElement {
    const dpad = document.createElement('div');
    dpad.className = 'gamepad-dpad';
    dpad.style.cssText = `
      position: absolute;
      left: 12px;
      bottom: calc(64px + env(safe-area-inset-bottom, 0px));
      width: 170px;
      height: 170px;
      pointer-events: none;
      touch-action: none;
    `;

    const createButton = (
      direction: string,
      top: string,
      left: string,
      symbol: string,
    ): HTMLElement => {
      const btn = document.createElement('div');
      btn.className = `dpad-${direction}`;
      btn.dataset.direction = direction;
      btn.style.cssText = `
        position: absolute;
        top: ${top};
        left: ${left};
        width: 56px;
        height: 56px;
        background: rgba(12, 24, 48, 0.5);
        border: 2px solid rgba(90, 150, 230, 0.9);
        border-radius: 14px;
        box-shadow: inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 6px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        color: rgba(255, 255, 255, 0.9);
        text-shadow: 0 1px 3px rgba(0,0,0,0.5);
        transition: background 0.05s, transform 0.05s;
        pointer-events: auto;
        touch-action: none;
      `;
      btn.textContent = symbol;

      btn.addEventListener('touchstart', (e) => this.handleDpadButtonStart(e, direction), {
        passive: false,
      });
      btn.addEventListener('touchend', (e) => this.handleDpadButtonEnd(e, direction), {
        passive: false,
      });
      btn.addEventListener('touchcancel', (e) => this.handleDpadButtonEnd(e, direction), {
        passive: false,
      });

      return btn;
    };

    const up = createButton('up', '0', '57px', '▲');
    const down = createButton('down', '114px', '57px', '▼');
    const left = createButton('left', '57px', '0', '◀');
    const right = createButton('right', '57px', '114px', '▶');

    dpad.appendChild(up);
    dpad.appendChild(down);
    dpad.appendChild(left);
    dpad.appendChild(right);

    return dpad;
  }

  private createButtons(): HTMLElement {
    const buttons = document.createElement('div');
    buttons.className = 'gamepad-buttons';
    buttons.style.cssText = `
      position: absolute;
      right: 12px;
      bottom: calc(64px + env(safe-area-inset-bottom, 0px));
      width: 150px;
      height: 140px;
      pointer-events: none;
      touch-action: none;
    `;

    const jumpBtn = document.createElement('div');
    jumpBtn.className = 'btn-jump';
    jumpBtn.style.cssText = `
      position: absolute;
      bottom: 4px;
      right: 0;
      width: 64px;
      height: 64px;
      background: rgba(47, 155, 53, 0.85);
      border: 3px solid rgba(95, 212, 99, 0.95);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: bold;
      color: white;
      text-shadow: 0 1px 3px rgba(0,0,0,0.5);
      transition: background 0.05s, transform 0.05s;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      pointer-events: auto;
      touch-action: none;
      flex-direction: column;
      line-height: 1.2;
    `;
    jumpBtn.innerHTML = 'JUMP<span style="font-size:9px;opacity:0.7">2x=FLIP</span>';

    const fireBtn = document.createElement('div');
    fireBtn.className = 'btn-fire';
    fireBtn.style.cssText = `
      position: absolute;
      bottom: 0;
      left: 0;
      width: 72px;
      height: 72px;
      background: rgba(196, 56, 44, 0.85);
      border: 3px solid rgba(244, 103, 92, 0.95);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: bold;
      color: white;
      text-shadow: 0 1px 3px rgba(0,0,0,0.5);
      transition: background 0.05s, transform 0.05s;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      pointer-events: auto;
      touch-action: none;
    `;
    fireBtn.textContent = 'FIRE';

    buttons.appendChild(jumpBtn);
    buttons.appendChild(fireBtn);

    jumpBtn.addEventListener('touchstart', this.handleJumpTouchStart.bind(this), {
      passive: false,
    });
    jumpBtn.addEventListener('touchend', this.handleJumpTouchEnd.bind(this), { passive: false });
    jumpBtn.addEventListener('touchcancel', this.handleJumpTouchEnd.bind(this), { passive: false });

    fireBtn.addEventListener('touchstart', this.handleFireTouchStart.bind(this), {
      passive: false,
    });
    fireBtn.addEventListener('touchend', this.handleFireTouchEnd.bind(this), { passive: false });
    fireBtn.addEventListener('touchcancel', this.handleFireTouchEnd.bind(this), { passive: false });

    return buttons;
  }

  /** True when the given tracked touch id is no longer on the screen. */
  private isStaleTouch(e: TouchEvent, id: number | null): boolean {
    return id !== null && !Array.from(e.touches).some((t) => t.identifier === id);
  }

  private handleDpadButtonStart(e: TouchEvent, direction: string): void {
    e.preventDefault();
    e.stopPropagation();

    if (this.isStaleTouch(e, this.dpadTouches.get(direction) ?? null)) {
      this.dpadTouches.delete(direction);
      this.state[direction as keyof GamepadState] = false;
    }

    const touch = e.changedTouches[0];
    if (touch) {
      this.dpadTouches.set(direction, touch.identifier);
      this.state[direction as keyof GamepadState] = true;
      this.updateVisualState();
      this.vibrate(8);
      this.callbacks.onStateChange(this.state);
    }
  }

  private handleDpadButtonEnd(e: TouchEvent, direction: string): void {
    e.preventDefault();
    e.stopPropagation();

    for (const touch of Array.from(e.changedTouches)) {
      if (this.dpadTouches.get(direction) === touch.identifier) {
        this.dpadTouches.delete(direction);
        this.state[direction as keyof GamepadState] = false;
        this.updateVisualState();
        this.callbacks.onStateChange(this.state);
      }
    }
  }

  private vibrate(duration: number = 10): void {
    if ('vibrate' in navigator) {
      navigator.vibrate(duration);
    }
  }

  private updateVisualState(): void {
    const directions = ['up', 'down', 'left', 'right'] as const;
    for (const dir of directions) {
      const btn = this.dpadContainer.querySelector(`.dpad-${dir}`) as HTMLElement;
      if (btn) {
        if (this.state[dir]) {
          btn.style.background = 'rgba(56, 198, 224, 0.85)';
          btn.style.transform = 'scale(0.9)';
          btn.style.borderColor = 'rgba(120, 230, 255, 1)';
        } else {
          btn.style.background = 'rgba(12, 24, 48, 0.5)';
          btn.style.transform = 'scale(1)';
          btn.style.borderColor = 'rgba(90, 150, 230, 0.9)';
        }
      }
    }

    const jumpBtn = this.buttonsContainer.querySelector('.btn-jump') as HTMLElement;
    if (jumpBtn) {
      if (this.state.jump) {
        jumpBtn.style.background = 'rgba(95, 212, 99, 0.98)';
        jumpBtn.style.transform = 'scale(0.9)';
        jumpBtn.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      } else {
        jumpBtn.style.background = 'rgba(47, 155, 53, 0.85)';
        jumpBtn.style.transform = 'scale(1)';
        jumpBtn.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
      }
    }

    const fireBtn = this.buttonsContainer.querySelector('.btn-fire') as HTMLElement;
    if (fireBtn) {
      if (this.state.fire) {
        fireBtn.style.background = 'rgba(244, 103, 92, 0.98)';
        fireBtn.style.transform = 'scale(0.9)';
        fireBtn.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      } else {
        fireBtn.style.background = 'rgba(196, 56, 44, 0.85)';
        fireBtn.style.transform = 'scale(1)';
        fireBtn.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
      }
    }
  }

  private handleJumpTouchStart(e: TouchEvent): void {
    e.preventDefault();

    if (this.isStaleTouch(e, this.jumpTouchId)) {
      this.jumpTouchId = null;
      this.state.jump = false;
    }

    for (const touch of Array.from(e.changedTouches)) {
      if (this.jumpTouchId === null) {
        this.jumpTouchId = touch.identifier;
        this.state.jump = true;
        this.updateVisualState();
        this.vibrate(15);

        const now = Date.now();
        if (now - this.lastJumpTime < this.DOUBLE_TAP_THRESHOLD) {
          if (this.jumpTimeoutId) {
            clearTimeout(this.jumpTimeoutId);
            this.jumpTimeoutId = null;
          }
          this.callbacks.onBackflip();
          this.lastJumpTime = 0;
        } else {
          this.lastJumpTime = now;
          this.jumpTimeoutId = setTimeout(() => {
            this.jumpTimeoutId = null;
            this.callbacks.onJump();
          }, this.DOUBLE_TAP_THRESHOLD);
        }
      }
    }
  }

  private handleJumpTouchEnd(e: TouchEvent): void {
    e.preventDefault();

    for (const touch of Array.from(e.changedTouches)) {
      if (touch.identifier === this.jumpTouchId) {
        this.jumpTouchId = null;
        this.state.jump = false;
        this.updateVisualState();
      }
    }
  }

  private handleFireTouchStart(e: TouchEvent): void {
    e.preventDefault();

    if (this.isStaleTouch(e, this.fireTouchId)) {
      this.fireTouchId = null;
      this.state.fire = false;
      this.callbacks.onFireEnd();
    }

    for (const touch of Array.from(e.changedTouches)) {
      if (this.fireTouchId === null) {
        this.fireTouchId = touch.identifier;
        this.state.fire = true;
        this.updateVisualState();
        this.vibrate(20);
        this.callbacks.onFireStart();
      }
    }
  }

  private handleFireTouchEnd(e: TouchEvent): void {
    e.preventDefault();

    for (const touch of Array.from(e.changedTouches)) {
      if (touch.identifier === this.fireTouchId) {
        this.fireTouchId = null;
        this.state.fire = false;
        this.updateVisualState();
        this.callbacks.onFireEnd();
      }
    }
  }

  show(): void {
    this.container.style.display = 'block';
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  isVisible(): boolean {
    return this.container.style.display !== 'none';
  }

  getState(): GamepadState {
    return { ...this.state };
  }

  destroy(): void {
    this.detachShootWindowListeners();
    this.container.remove();
  }
}
